import { useEffect, useRef, useState } from "react";
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsRightLeftIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

// Beleg-Anzeige mit PDF.js (F21): gleiche Darstellung in Chrome, Safari und auf dem iPhone, mit Zoom, Herunterladen und neuem Tab
// (Drucken über den neuen Tab). Fällt auf den Browser-Viewer (iframe) zurück, wenn PDF.js nicht lädt oder die Datei nicht lesbar ist.
// Die Höhe kommt vom Elternelement (Mobil 55vh, Desktop Vollhöhe); gescrollt wird in der Fläche.

const ZOOM_STUFEN = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

export function BelegViewer({ url, title, className }: { url: string; title: string; className?: string }) {
  const flaeche = useRef<HTMLDivElement>(null);
  const seiten = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [breite, setBreite] = useState(0);
  const [fehler, setFehler] = useState(false);
  const [laedt, setLaedt] = useState(true);

  // Breite der Fläche beobachten (Fenster, Drehen, Overlay) — Neuzeichnen nur bei echter Änderung
  useEffect(() => {
    const el = flaeche.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const w = Math.floor(e!.contentRect.width);
      setBreite((alt) => (Math.abs(alt - w) > 4 ? w : alt));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!breite || fehler) return;
    let abgebrochen = false;
    setLaedt(true);
    (async () => {
      try {
        const { ladePdf, zeichneSeite } = await import("../lib/pdfRender");
        const { pdf, schliessen } = await ladePdf(url);
        try {
          const canvases: HTMLCanvasElement[] = [];
          for (let nr = 1; nr <= pdf.numPages; nr++) {
            if (abgebrochen) return;
            canvases.push(await zeichneSeite(pdf, nr, Math.max(100, (breite - 16) * zoom)));
          }
          if (abgebrochen || !seiten.current) return;
          seiten.current.replaceChildren(...canvases.map((c) => { c.className = "block mx-auto mb-2 bg-white shadow-sm"; return c; }));
          setLaedt(false);
        } finally {
          void schliessen();
        }
      } catch (e) {
        console.warn("[BelegViewer] PDF.js fehlgeschlagen, zeige Browser-Ansicht", e);
        if (!abgebrochen) setFehler(true);
      }
    })();
    return () => { abgebrochen = true; };
  }, [url, breite, zoom, fehler]);

  const stufe = ZOOM_STUFEN.indexOf(zoom);
  const knopf = "p-1.5 rounded-ios text-ios-muted hover:text-ios-ink hover:bg-ios-bg disabled:opacity-40";

  return (
    <div className={`flex flex-col h-full ${className ?? ""}`}>
      <div className="flex items-center gap-1 pb-1.5 text-[13px]">
        {!fehler && (
          <>
            <button type="button" className={knopf} title="Verkleinern" aria-label="Verkleinern" disabled={stufe <= 0}
              onClick={() => setZoom(ZOOM_STUFEN[Math.max(0, stufe - 1)]!)}><MagnifyingGlassMinusIcon className="w-5 h-5" /></button>
            <span className="tabular-nums w-11 text-center text-ios-muted">{Math.round(zoom * 100)} %</span>
            <button type="button" className={knopf} title="Vergrößern" aria-label="Vergrößern" disabled={stufe >= ZOOM_STUFEN.length - 1}
              onClick={() => setZoom(ZOOM_STUFEN[Math.min(ZOOM_STUFEN.length - 1, stufe + 1)]!)}><MagnifyingGlassPlusIcon className="w-5 h-5" /></button>
            <button type="button" className={knopf} title="Breite einpassen" aria-label="Breite einpassen" disabled={zoom === 1}
              onClick={() => setZoom(1)}><ArrowsRightLeftIcon className="w-5 h-5" /></button>
          </>
        )}
        <span className="flex-1" />
        <a className={knopf} href={url} download title="Herunterladen" aria-label="Herunterladen"><ArrowDownTrayIcon className="w-5 h-5" /></a>
        <a className={knopf} href={url} target="_blank" rel="noopener" title="In neuem Tab öffnen (dort drucken)" aria-label="In neuem Tab öffnen">
          <ArrowTopRightOnSquareIcon className="w-5 h-5" /></a>
      </div>
      <div ref={flaeche} className="flex-1 min-h-0 overflow-auto rounded-ios bg-ios-bg/50 p-2">
        {fehler ? (
          <iframe src={`${url}#navpanes=0`} title={title} className="w-full h-full border-0 rounded-ios" />
        ) : (
          <>
            {laedt && <div className="text-[13px] text-ios-muted text-center py-6">Beleg wird geladen …</div>}
            <div ref={seiten} aria-label={title} role="img" />
          </>
        )}
      </div>
    </div>
  );
}
